import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, Plus, Save, TriangleAlert } from 'lucide-react';
import { cogsPostingService } from '../../services/cogsPostingService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import PickerPopover from '../../components/PickerPopover';

// =============================================================================
// Manufacturing › Budget Setup › COGS Posting   (LIVE)
//
// WHERE MATERIAL AND LABOUR COST LAND IN THE LEDGER. The same construction as
// Revenue › Revenue Posting, deliberately: catalogue, not capture. Deciding
// that protein consumption is GL 5101 is a chart-of-accounts decision with one
// owner, and leaving it on the entry screen is how two plants post the same
// ingredient to two accounts inside a week.
//
// THESE TWO CATALOGUES ARE WHY COGS IS 35.9m AND NOT 6.0m. Before they were
// populated, vw_bud_material_cost and vw_bud_labour_cost resolved no account,
// so every row fell out of the spine and the P&L showed a factory that consumed
// nothing. An empty catalogue is not an absence of cost — it is cost that posts
// NOWHERE, which is why the unposted figure sits at the top of this page rather
// than being left for somebody to discover by subtraction.
//
// TWO TABS, ONE TABLE EACH. Not a filter over one table — materials and labour
// are genuinely different tables with different scope columns. (The lesson from
// Revenue Posting was the opposite one: do NOT split a single table across tabs
// on a filter. These are two tables, so two tabs is right.)
//
// THE LABOUR TAB IS PREMIUMS ONLY — OVERTIME AND ALLOWANCES.
//
// Base pay is deliberately absent, and this page was wrong when it first
// shipped: it offered a catalogue over ALL labour without noticing Mantle
// already configured posting in two other places — People › GL Mapping (site +
// job title, which is base pay and HR's decision) and the Variable Labour page,
// whose functions already carry separate overtime and allowance accounts.
//
// vw_bud_labour_cost now resolves base pay WITHOUT ever reading this table, and
// bud_labour_posting has a CHECK constraint refusing an ORDINARY rule — because
// a rule that is stored, displayed and silently never applied is worse than one
// that was refused.
//
// RESOLUTION IS BY WEIGHT, MOST SPECIFIC WINS, and rows are shown BROADEST
// FIRST so the grid reads down in the order the LATERAL actually resolves:
//
//   materials   stock code 8 > stock group 4 > product class 2 > site 1
//   labour      pay category 8 > component type 4 > function 2 > site 1
//
// THE LABOUR WEIGHTS ARE THE TRAP. A rule naming only a function CANNOT beat
// one naming a pay category. To send ordinary extrusion hours somewhere
// specific, the rule has to name both — which is why the demo catalogue's first
// two rows carry two columns and not one. The page says so on screen, because
// getting it wrong produces a rule that looks right and never fires.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white disabled:opacity-40 transition-colors';

const fmtR = (n) => Math.round(Number(n) || 0).toLocaleString();

const T_NEW = { bgCell: 'rgba(16, 185, 129, 0.10)' };
const T_MUTED = { textDark: '#94a3b8' };

// The scope columns, per tab. `all` is what an unset column reads as — "All
// sites", "All classes" — so a broad rule states its breadth rather than
// showing an empty cell somebody has to interpret.
const TABS = {
  material: {
    label: 'Materials',
    kind: 'material',
    source: 'vw_bud_material_cost',
    blurb: 'Material consumption — production volumes valued at cost, from the BOM.',
    scope: [
      { id: 'site_code', title: 'Site', width: 150, all: 'All sites', pick: 'sites' },
      { id: 'product_class', title: 'Product Class', width: 160, all: 'All classes', pick: 'productClasses' },
      { id: 'stock_group', title: 'Stock Group', width: 160, all: 'All groups', pick: 'stockGroups' },
      { id: 'stock_code', title: 'Stock Code', width: 170, all: 'All items', pick: 'stockCodes' },
    ],
    weights: 'stock code 8 · stock group 4 · product class 2 · site 1',
  },
  labour: {
    label: 'Overtime & Allowances',
    kind: 'labour',
    source: 'vw_bud_labour_cost',
    blurb: 'Overtime, public holidays and shift allowances — the premium the factory pays over base rates.',
    // BASE PAY IS NOT EDITABLE HERE, by ruling and by database constraint. It
    // is a contract of employment: one person, one rate, owned by HR, and the
    // same number whichever department they worked in this month. A shift
    // premium is a PRODUCTION decision — it exists because the factory ran
    // nights — so the factory decides where it lands.
    excludePayCategories: ['ORDINARY'],
    scope: [
      { id: 'site_code', title: 'Site', width: 150, all: 'All sites', pick: 'sites' },
      { id: 'function_code', title: 'Function', width: 160, all: 'All functions', pick: 'functions' },
      { id: 'component_type', title: 'Component', width: 150, all: 'All components', pick: 'componentTypes' },
      { id: 'pay_category', title: 'Pay Category', width: 160, all: 'All categories', pick: 'payCategories' },
    ],
    weights: 'pay category 8 · component type 4 · function 2 · site 1',
  },
};

const EMPTY = { material: [], labour: [], picks: {}, unposted: [] };
const newRow = () => ({ _new: true, _key: `new-${Math.random().toString(36).slice(2)}` });

export default function CogsPosting() {
  const navigate = useNavigate();
  const { selectedVersion } = useAppContext();

  const [tab, setTab] = useState('material');
  const [data, setData] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [picker, setPicker] = useState(null);

  const spec = TABS[tab];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await cogsPostingService.get();
      setData(res.data || EMPTY);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the posting catalogues.');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  // Re-seed the working rows whenever the tab or the loaded data changes.
  useEffect(() => {
    setRows((data[tab] || []).map((r) => ({ ...r, _key: r.posting_id })));
  }, [data, tab]);

  // Memoised: a fresh {} each render would re-create every picker callback
  // that depends on it, on every keystroke.
  const picks = useMemo(() => data.picks || {}, [data.picks]);

  // Options for a scope column, normalised to { code, name } whatever the
  // source shape is — some pick lists are plain strings, some are rows.
  const optionsFor = useCallback((col) => {
    const src = picks[col.pick] || [];
    if (!src.length) return [];
    if (typeof src[0] === 'string') return src.map((s) => ({ code: s, description: '' }));
    if (col.pick === 'sites') return src.map((s) => ({ code: s.site_code, description: s.site_name }));
    if (col.pick === 'stockCodes') return src.map((s) => ({ code: s.stock_code, description: s.description }));
    return src.map((s) => ({ code: s.code || s.value, description: s.name || s.description || '' }));
  }, [picks]);

  const accountOptions = useMemo(
    // The P&L section rides along in the description: it is what tells you
    // whether an account is a sensible target, and going to look it up
    // elsewhere is how a material cost ends up in overheads.
    () => (picks.accounts || []).map((a) => ({
      code: a.account_code,
      description: `${a.account_description}${a.pnl_section ? ` · ${a.pnl_section}` : ''}`,
    })),
    [picks.accounts]
  );
  const costCentreOptions = useMemo(
    () => (picks.costCenters || []).map((c) => ({ code: c.cost_center_code, description: c.description })),
    [picks.costCenters]
  );

  const columns = useMemo(() => ([
    ...spec.scope.map((c) => ({ id: c.id, title: c.title, width: c.width })),
    { id: 'account_code', title: 'GL Account', width: 260 },
    { id: 'cost_center_code', title: 'Cost Centre', width: 200 },
    { id: 'notes', title: 'Notes', width: 220, grow: 1 },
    { id: 'actions', title: '', width: 44 },
  ]), [spec]);

  const colIds = useMemo(() => columns.map((c) => c.id), [columns]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = rows[row];
    if (!r) return gcell.text('', { readonly: true });
    const theme = r._new ? T_NEW : undefined;

    if (id === 'actions') return gcell.text('✕', { readonly: true, theme });

    const scopeCol = spec.scope.find((c) => c.id === id);
    if (scopeCol) {
      const v = r[id];
      return gcell.text(v || scopeCol.all, { readonly: true, theme: v ? theme : (theme || T_MUTED) });
    }

    if (id === 'account_code') {
      const label = r.account_code
        ? `${r.account_code}${r.account_description ? ` · ${r.account_description}` : ''}`
        : 'Required — pick an account';
      return gcell.text(label, { readonly: true, theme: r.account_code ? theme : (theme || T_MUTED) });
    }
    if (id === 'cost_center_code') {
      const label = r.cost_center_code
        ? `${r.cost_center_code}${r.cost_center_description ? ` · ${r.cost_center_description}` : ''}`
        : 'From the cost line';
      return gcell.text(label, { readonly: true, theme: r.cost_center_code ? theme : (theme || T_MUTED) });
    }
    return gcell.text(r.notes || '', { readonly: false, theme });
  }, [colIds, rows, spec]);

  const rowsRef = useRef([]);
  rowsRef.current = rows;

  // Everything except Notes is a PICKER, never typed. A typo in a stock group
  // creates a rule that can never match anything, and nothing downstream would
  // ever say so.
  const onCellClicked = useCallback(([col, row], event) => {
    const id = colIds[col];
    const r = rowsRef.current[row];
    if (!r) return;
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };

    if (id === 'actions') {
      setRows((prev) => prev.filter((x) => x._key !== r._key));
      if (!r._new) {
        cogsPostingService.remove(spec.kind, r.posting_id)
          .then((res) => setNotice(res.message || 'Rule removed.'))
          .catch((e) => setError(e.response?.data?.message || 'Could not remove the rule.'));
      }
      return;
    }
    if (id === 'notes') return;

    const scopeCol = spec.scope.find((c) => c.id === id);
    let options = id === 'account_code' ? accountOptions
      : id === 'cost_center_code' ? costCentreOptions
        : optionsFor(scopeCol);
    // Base pay cannot be scoped here — the view never reads this table for it
    // and the database refuses the row, so it must not be offerable.
    if (id === 'pay_category' && spec.excludePayCategories) {
      options = options.filter((o) => !spec.excludePayCategories.includes(o.code));
    }

    setPicker({
      rowKey: r._key,
      field: id,
      // No clear row on the account: a rule without one resolves to nothing and
      // the cost posts nowhere, so it must not be one click away.
      clearLabel: id === 'account_code' ? null : (scopeCol ? scopeCol.all : 'From the cost line'),
      options,
      ...pos,
    });
  }, [colIds, spec, accountOptions, costCentreOptions, optionsFor]);

  const onCellEdited = useCallback(([col, row], v) => {
    if (colIds[col] !== 'notes') return;
    const r = rowsRef.current[row];
    setRows((prev) => prev.map((x) => (x._key === r._key ? { ...x, notes: v?.data ?? '' } : x)));
  }, [colIds]);

  const applyPick = (value, label) => {
    setRows((prev) => prev.map((r) => {
      if (r._key !== picker.rowKey) return r;
      const next = { ...r, [picker.field]: value };
      if (picker.field === 'account_code') next.account_description = label || '';
      if (picker.field === 'cost_center_code') next.cost_center_description = label || '';
      // A stock code already belongs to its group and class — the server
      // rejects a rule naming both, so clear them here rather than letting
      // somebody build a row that cannot be saved.
      if (picker.field === 'stock_code' && value) { next.stock_group = null; next.product_class = null; }
      if ((picker.field === 'stock_group' || picker.field === 'product_class') && value) next.stock_code = null;
      return next;
    }));
    setPicker(null);
  };

  const save = async () => {
    const missing = rows.filter((r) => !r.account_code);
    if (missing.length) {
      setError(`${missing.length} row${missing.length === 1 ? ' has' : 's have'} no GL account. A rule with no account resolves to nothing and the cost posts nowhere.`);
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await cogsPostingService.save(spec.kind, rows.map((r) => {
        const out = { account_code: r.account_code, cost_center_code: r.cost_center_code, notes: r.notes };
        spec.scope.forEach((c) => { out[c.id] = r[c.id] || null; });
        return out;
      }));
      setNotice(res.message || 'Saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const unposted = useMemo(
    () => (data.unposted || []).filter((u) => u.source_table === spec.source
      || (spec.kind === 'labour' && u.source_table === 'calc_labour_costs')),
    [data.unposted, spec]
  );
  const unpostedTotal = unposted.reduce((s, u) => s + u.unposted_amount, 0);

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to edit the posting catalogues.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/manufacturing')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Manufacturing
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              COGS Posting
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
          </div>
          <div className="flex items-center gap-2">
            <button className={btn} onClick={() => setRows((p) => [...p, newRow()])}>
              <Plus className="w-3.5 h-3.5" /> Add rule
            </button>
            <button className={btnPrimary} onClick={save} disabled={saving || loading}>
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-1 border-b border-slate-200 dark:border-slate-700">
          {Object.entries(TABS).map(([k, t]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                tab === k
                  ? 'border-slate-900 text-slate-900 dark:border-slate-200 dark:text-slate-100'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}>
              {t.label}
              <span className="ml-1.5 text-[10px] font-normal text-slate-400">{(data[k] || []).length}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-emerald-700 dark:text-emerald-300">{notice}</p>
          </div>
        )}

        {/* Cost that posts NOWHERE. At the top, because a thin COGS figure with
            no explanation is the exact problem this page exists to prevent. */}
        {unpostedTotal !== 0 && (
          <div className="rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-red-700 dark:text-red-300 flex items-center gap-1.5">
              <TriangleAlert className="w-3.5 h-3.5" />
              {fmtR(unpostedTotal)} of {spec.label.toLowerCase()} cost resolves to no account
            </p>
            <p className="m-0 mt-0.5 text-[11px] text-red-700/80 dark:text-red-300/80">
              It is costed correctly and absent from the P&amp;L, the cash flow and the balance sheet. A rule below
              that covers it fixes all three at once.
            </p>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 px-6 text-center">
              <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
                No {spec.label.toLowerCase()} rules yet — every {spec.label.toLowerCase()} cost in this version posts nowhere.
              </p>
              <button className={`${btn} mt-3`} onClick={() => setRows([newRow()])}>
                <Plus className="w-3.5 h-3.5" /> Add the first rule
              </button>
            </div>
          ) : (
            <MantleDataGrid
              gridId={`cogs-posting-${tab}`}
              fitHeight
              fitHeightPadding={6}
              columns={columns}
              rowCount={rows.length}
              getCellContent={getCellContent}
              onCellClicked={onCellClicked}
              onCellEdited={onCellEdited}
              freezeColumns={1}
              hint="Click any cell except Notes to pick a value — nothing here is typed, because a typo makes a rule that can never match. Rows are shown broadest-first, the order the resolution actually runs in."
              toolbar={
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  most specific wins · {spec.weights}
                </span>
              }
            />
          )}
        </div>

        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          {spec.blurb} Each cost line takes the <strong>most specific</strong> rule that matches it
          ({spec.weights}); an unset column means &ldquo;any&rdquo;, so the top rule is the catch-all and every rule
          below it is an exception.
        </p>
        {tab === 'labour' && (
          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>Base pay is not set here.</strong> Ordinary hours are a contract of employment, so they post from
            <button className="mx-1 underline hover:text-slate-600 dark:hover:text-slate-300" onClick={() => navigate('/people/gl-mapping')}>People &rsaquo; GL Mapping</button>
            (site and job title), falling back to the function&rsquo;s own account on the Variable Labour page. This
            tab covers only the premium the factory pays on top &mdash; and an ordinary-hours rule is refused rather
            than stored and ignored.
          </p>
        )}
        {tab === 'labour' && (
          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>The one that catches people out:</strong> pay category outranks function, so a rule naming only a
            function cannot beat one naming only a pay category. To send <em>ordinary extrusion hours</em> somewhere
            specific, the rule has to name <em>both</em> &mdash; function and pay category together (2 + 8) beat a
            pay-category rule on its own.
          </p>
        )}
        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          Leave the cost centre blank and the cost keeps whichever cost centre the line already carries &mdash; which
          is usually what you want, because that is the plant or department that actually incurred it.
        </p>
      </div>

      {picker && (
        <PickerPopover
          anchor={{ x: picker.x, y: picker.y }}
          options={picker.options}
          placeholder="Search…"
          emptyText="Nothing matches."
          onPick={(o) => applyPick(o.code, o.description)}
          onClear={picker.clearLabel ? () => applyPick(null, '') : undefined}
          clearLabel={picker.clearLabel}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}
